import React, { useState, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LanguageContext } from '../context/LanguageContext';

// 导航链接数据，更新为新的游戏类型，保持彩虹色彩
const navLinks = {
  'zh-CN': [
    { name: '全部', path: '/', color: '#ff7675' },
    { name: '动作', path: '/?category=动作', color: '#74b9ff' },
    { name: '益智', path: '/?category=益智', color: '#fdcb6e' },
    { name: '策略', path: '/?category=策略', color: '#55efc4' },
    { name: '体育', path: '/?category=体育', color: '#a29bfe' },
    { name: '休闲', path: '/?category=休闲', color: '#ff9ff3' },
    { name: '多人', path: '/?category=多人', color: '#ffeaa7' },
    { name: '教育', path: '/?category=教育', color: '#81ecec' },
    { name: '角色扮演', path: '/?category=角色扮演', color: '#fab1a0' },
  ],
  'en-US': [
    { name: 'All', path: '/', color: '#ff7675' },
    { name: 'Action', path: '/?category=Action', color: '#74b9ff' },
    { name: 'Puzzle', path: '/?category=Puzzle', color: '#fdcb6e' },
    { name: 'Strategy', path: '/?category=Strategy', color: '#55efc4' },
    { name: 'Sports', path: '/?category=Sports', color: '#a29bfe' },
    { name: 'Casual', path: '/?category=Casual', color: '#ff9ff3' },
    { name: 'Multiplayer', path: '/?category=Multiplayer', color: '#ffeaa7' },
    { name: 'Educational', path: '/?category=Educational', color: '#81ecec' },
    { name: 'RPG', path: '/?category=RPG', color: '#fab1a0' },
  ]
};

const Header = () => {
  const { language, setLanguage } = useContext(LanguageContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // 处理搜索表单提交
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?keyword=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setIsMenuOpen(false);
    }
  };

  // 切换语言
  const changeLanguage = (lang) => {
    setLanguage(lang);
    setIsLangMenuOpen(false);
  };

  // 检查导航项是否激活
  const isActive = (path) => {
    if (path === '/' && location.pathname === '/' && !location.search) return true;
    
    // 处理带有查询参数的路径
    if (path.includes('?category=')) {
      const categoryParam = new URLSearchParams(path.split('?')[1]).get('category');
      const currentCategoryParam = new URLSearchParams(location.search).get('category');
      return categoryParam === currentCategoryParam;
    }
    
    // 旧的路径检测逻辑保留，兼容旧格式
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    
    return false;
  };

  // 根据当前语言获取网站名称
  const getWebsiteName = () => {
    return language === 'zh-CN' ? '顶尖小游戏' : 'Top Mini Games';
  };

  return (
    <header className="sticky top-0 z-50 shadow-sm">
      {/* 简化的顶部导航条 */}
      <div className="bg-gray-900 py-1 px-4">
        <div className="container mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center">
            <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-pink-500 via-blue-500 to-green-500">
              {getWebsiteName()}
            </span>
          </Link>
          
          {/* 移动端菜单按钮 - 始终可见 */}
          <div className="flex items-center gap-2">
            <button
              className="sm:hidden text-white focus:outline-none"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "关闭菜单" : "打开菜单"}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          
            {/* 语言切换下拉菜单 */}
            <div className="relative">
              <button
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                onMouseEnter={() => setIsLangMenuOpen(true)}
                className="flex items-center text-white hover:text-gray-200 text-sm transition-colors gap-1"
              >
                <span>{language === 'zh-CN' ? '语言' : 'Language'}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
              
              {isLangMenuOpen && (
                <div 
                  className="absolute right-0 mt-2 w-36 bg-white rounded-md shadow-lg py-1 z-50"
                  onMouseLeave={() => setIsLangMenuOpen(false)}
                >
                  <button
                    onClick={() => changeLanguage('zh-CN')}
                    className={`block w-full text-left px-4 py-2 text-sm ${language === 'zh-CN' ? 'bg-gray-100 text-gray-900 font-medium' : 'text-gray-700 hover:bg-gray-100'}`}
                  >
                    中文
                  </button>
                  <button
                    onClick={() => changeLanguage('en-US')}
                    className={`block w-full text-left px-4 py-2 text-sm ${language === 'en-US' ? 'bg-gray-100 text-gray-900 font-medium' : 'text-gray-700 hover:bg-gray-100'}`}
                  >
                    English
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 彩虹胶囊式导航栏 - 移动端和桌面端显示逻辑 */}
      <div className="bg-white py-3 px-4">
        <div className="container mx-auto">
          {/* 大屏幕导航 */}
          <div className={`hidden md:flex items-center justify-center flex-wrap gap-2`}>
            {navLinks[language].map((link) => {
              const active = isActive(link.path);
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className="transition-all duration-300 rounded-full px-4 py-1.5 text-sm font-medium"
                  style={{ 
                    backgroundColor: active ? link.color : 'white',
                    color: active ? 'white' : '#4a5568',
                    border: `2px solid ${link.color}`
                  }}
                >
                  {link.name}
                </Link>
              );
            })}
          </div>

          {/* 小屏固定显示部分导航 */}
          <div className="md:hidden flex overflow-x-auto no-scrollbar py-2 gap-1.5">
            {navLinks[language].slice(0, 4).map((link) => {
              const active = isActive(link.path);
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className="flex-shrink-0 transition-all duration-300 rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap"
                  style={{ 
                    backgroundColor: active ? link.color : 'white',
                    color: active ? 'white' : '#4a5568',
                    border: `2px solid ${link.color}`
                  }}
                >
                  {link.name}
                </Link>
              );
            })}
            {/* 更多按钮 */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="flex-shrink-0 transition-all duration-300 rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap border-2 border-gray-400"
            >
              {language === 'zh-CN' ? '更多...' : 'More...'}
            </button>
          </div>

          {/* 全屏搜索框 */}
          <div className="flex justify-center mt-4">
            <form onSubmit={handleSearch} className="w-full md:w-2/3 lg:w-1/2">
              <div className="flex">
                <input
                  type="text"
                  placeholder={language === 'zh-CN' ? "搜索游戏..." : "Search games..."}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="border border-gray-300 rounded-l-md text-gray-700 px-3 py-2 outline-none focus:ring-2 focus:ring-gray-400 w-full"
                />
                <button
                  type="submit"
                  className="bg-gray-800 hover:bg-gray-900 transition-colors px-4 rounded-r-md"
                >
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* 移动端展开菜单 */}
      {isMenuOpen && (
        <div className="md:hidden pb-4 bg-white p-4 border-t border-gray-100">
          <div className="flex flex-col space-y-2">
            {navLinks[language].map((link) => {
              const active = isActive(link.path);
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className="flex items-center transition-all duration-300 rounded-full px-4 py-2 text-sm font-medium"
                  style={{ 
                    backgroundColor: active ? link.color : 'white',
                    color: active ? 'white' : '#4a5568',
                    border: `2px solid ${link.color}`
                  }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </header>
  );
};

// 添加禁用滚动条的全局样式
const style = document.createElement('style');
style.textContent = `
  .no-scrollbar::-webkit-scrollbar {
    display: none;
  }
  .no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;
document.head.appendChild(style);

export default Header; 